import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { useAuthStore } from '@/stores/authStore';
import { useSnackbar } from '@/hooks/useSnackbar';
import { log } from '@/lib/utils/logger';

interface RoleSwitcherDialogProps {
  open: boolean;
  onClose: () => void;
}

export function RoleSwitcherDialog({ open, onClose }: RoleSwitcherDialogProps) {
  const [selectedRole, setSelectedRole] = useState<number | null>(null);
  const { switchRole, currentUser } = useAuthStore();
  const { showSnackbar } = useSnackbar();

  // Compute filteredRoles during render - derived from currentUser.roles
  const filteredRoles: { id: number; name: string }[] =
    currentUser?.roles
      ?.filter((role): role is { id: number; name: string } => role.id !== null && role.name !== null)
      .map((role) => ({
        id: role.id,
        name: role.name,
      })) ?? [];

  const handleRoleChange = (event: SelectChangeEvent) => {
    setSelectedRole(Number(event.target.value));
    log.info('[RoleSwitcherDialog] Role selected:', Number(event.target.value));
  };

  const handleSwitch = async () => {
    if (selectedRole === null || selectedRole === currentUser?.selectedRole) return;
    log.info('[RoleSwitcherDialog] Attempting to switch from', currentUser?.selectedRole, 'to', selectedRole);
    try {
      await switchRole(selectedRole);
      log.info('[RoleSwitcherDialog] Role switched to', selectedRole);
      const roleName = filteredRoles.find((r) => r.id === selectedRole)?.name ?? 'role';
      showSnackbar(`Switched to ${roleName}`, 'success');
      onClose();
    } catch (err) {
      log.error('[RoleSwitcherDialog] Role switch failed:', err);
      showSnackbar('Failed to switch role', 'error');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="role-switcher-title"
      aria-describedby="role-switcher-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="role-switcher-title">Please select your user role</DialogTitle>

      <DialogContent>
        <Typography id="role-switcher-description" variant="body2" color="text.secondary" gutterBottom>
          Please select the role you want to use for this LiST session.
        </Typography>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel id="role-select-label">Role</InputLabel>
          <Select
            labelId="role-select-label"
            value={selectedRole !== null ? String(selectedRole) : ''} // Convert number to string for compatibility
            onChange={handleRoleChange} // Use the updated function
          >
            {filteredRoles.map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {role.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary" variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSwitch}
          color="primary"
          variant="contained"
          disabled={selectedRole === null || selectedRole === currentUser?.selectedRole}
        >
          Switch Role
        </Button>
      </DialogActions>
    </Dialog>
  );
}
